
import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ArrowLeft, Save, LayoutGrid, FolderPlus, FolderOpen, RefreshCw, Lock, Edit2, Check, X, FileSpreadsheet, Folder, ChevronDown, ChevronRight, File } from 'lucide-react';
import { User, CustomDeck, CustomDeckFolder } from '../types';
import { storage } from '../storage';
import * as XLSX from 'xlsx';


interface CustomDeckEditorProps {
  user: User;
  onUpdateUser: (updatedUser: User) => void;
  onBack: () => void;
}

const CustomDeckEditor: React.FC<CustomDeckEditorProps> = ({ user, onUpdateUser, onBack }) => {
  const [decks, setDecks] = useState<CustomDeck[]>(user.customDecks || []);
  const [folders, setFolders] = useState<CustomDeckFolder[]>(user.customFolders || []);
  
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(
      decks.length > 0 ? decks[0].id : null
  );
  
  const [newDeckName, setNewDeckName] = useState('');
  const [isCreatingDeck, setIsCreatingDeck] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  
  const [newTrick, setNewTrick] = useState('');
  
  const [editingDeckId, setEditingDeckId] = useState<string | null>(null);
  const [editDeckName, setEditDeckName] = useState('');
  
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');

  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  
  const [draggedDeckId, setDraggedDeckId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine what is currently selected
  
  // Get the tricks for the current selection
  const getCurrentTricks = (): string[] => {
      if (!selectedDeckId) return [];
      const deck = decks.find(d => d.id === selectedDeckId);
      return deck ? deck.tricks : [];
  };
  
  const getCurrentDeckName = (): string => {
      return decks.find(d => d.id === selectedDeckId)?.name || '';
  };

  const currentTricks = getCurrentTricks();

  // --- Deck Management ---

  const handleCreateDeck = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeckName.trim()) return;

    const newDeck: CustomDeck = {
      id: Date.now().toString(),
      name: newDeckName.trim(),
      tricks: [],
      folderId: null
    };

    const updatedDecks = [...decks, newDeck];
    updateUser(updatedDecks, folders);
    setNewDeckName('');
    setIsCreatingDeck(false);
    setSelectedDeckId(newDeck.id);
  };

  const handleDeleteDeck = (deckId: string) => {
    if (!confirm("Are you sure you want to delete this custom deck?")) return;
    const updatedDecks = decks.filter(d => d.id !== deckId);
    
    // Switch selection if we deleted the active one
    let nextSelection = selectedDeckId;
    if (selectedDeckId === deckId) {
        nextSelection = updatedDecks.length > 0 ? updatedDecks[0].id : null;
    }
    
    updateUser(updatedDecks, folders);
    setSelectedDeckId(nextSelection);
  };

  const handleRenameDeck = (deckId: string) => {
      if (!editDeckName.trim()) {
          setEditingDeckId(null);
          return;
      }
      const updatedDecks = decks.map(d => d.id === deckId ? { ...d, name: editDeckName.trim() } : d);
      updateUser(updatedDecks, folders);
      setEditingDeckId(null);
  };

  // --- Folder Management ---
  const handleCreateFolder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    const newFolder: CustomDeckFolder = {
      id: 'f_' + Date.now().toString(),
      name: newFolderName.trim(),
    };

    updateUser(decks, [...folders, newFolder]);
    setNewFolderName('');
    setIsCreatingFolder(false);
  };

  const handleDeleteFolder = (folderId: string) => {
    if (!confirm("Delete this folder? Decks inside will be moved to the root. / 删除此文件夹？其中的招式表将移至根目录。")) return;
    const updatedFolders = folders.filter(f => f.id !== folderId);
    const updatedDecks = decks.map(d => d.folderId === folderId ? { ...d, folderId: null } : d);
    updateUser(updatedDecks, updatedFolders);
  };

  const handleRenameFolder = (folderId: string) => {
      if (!editFolderName.trim()) {
          setEditingFolderId(null);
          return;
      }
      const updatedFolders = folders.map(f => f.id === folderId ? { ...f, name: editFolderName.trim() } : f);
      updateUser(decks, updatedFolders);
      setEditingFolderId(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !selectedDeckId) return;
      
      const reader = new FileReader();
      reader.onload = (evt) => {
          try {
              const ab = evt.target?.result;
              const wb = XLSX.read(ab, { type: 'array' });
              const wsname = wb.SheetNames[0];
              const ws = wb.Sheets[wsname];
              const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
              
              const newTricks: string[] = [];
              let addedCount = 0;
              
              data.forEach((row: any) => {
                  if (Array.isArray(row) && row.length > 0) {
                      // Attempt to decode any garbled unicode sequences from Excel strings if present
                      let name = String(row[0]).trim();
                      try {
                          name = name.replace(/(?:\\u|%u|u)([0-9a-fA-F]{4})/g, (match, hex) => {
                              return String.fromCharCode(parseInt(hex, 16));
                          });
                      } catch (e) {}
                      if (name && !['Name', 'name', '姓名', '招式', 'Trick', 'trick'].includes(name)) {
                          newTricks.push(name);
                          addedCount++;
                      }
                  }
              });

              if (newTricks.length === 0) {
                  alert('No valid tricks found. Please ensure the Excel file has tricks in the first column. / 未找到有效的招式。请确保Excel的第一列包含招式。');
                  return;
              }

              const combinedTricks = [...currentTricks, ...newTricks].slice(0, 120); // Cap at 120
              saveTricks(selectedDeckId, combinedTricks);
              
              alert(`Successfully imported ${addedCount} tricks! (Max 120 per deck) / 成功导入 ${addedCount} 个招式！(每组最多120个)`);
              
              if (fileInputRef.current) fileInputRef.current.value = '';
          } catch (err: any) {
              alert('Error parsing Excel file / 解析Excel出错: ' + err.message);
          }
      };
      reader.readAsArrayBuffer(file);
  };

  // --- Trick Management ---

  const handleAddTrick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeckId) return;
    if (!newTrick.trim()) return;
    if (currentTricks.length >= 120) {
      alert("Maximum of 120 tricks per deck allowed.");
      return;
    }
    
    const updatedTricks = [...currentTricks, newTrick.trim()];
    saveTricks(selectedDeckId, updatedTricks);
    setNewTrick('');
  };

  const handleDeleteTrick = (index: number) => {
    if (!selectedDeckId) return;
    const updatedTricks = currentTricks.filter((_, i) => i !== index);
    saveTricks(selectedDeckId, updatedTricks);
  };

  // --- Persistence Helpers ---

  const saveTricks = (deckId: string, tricks: string[]) => {
      const updatedDecks = decks.map(d => d.id === deckId ? { ...d, tricks } : d);
      updateUser(updatedDecks, folders);
  };

  const updateUser = (updatedDecks: CustomDeck[], updatedFolders: CustomDeckFolder[]) => {
    setDecks(updatedDecks);
    setFolders(updatedFolders);
    
    const updatedUser: User = { 
        ...user, 
        customDecks: updatedDecks,
        customFolders: updatedFolders
    };
    
    // Optimistic UI update
    onUpdateUser(updatedUser);
    
    // Async save to Firestore (fire and forget for UI responsiveness)
    storage.save(updatedUser);
  };

  const renderDeckItem = (deck: CustomDeck) => {
    return (
      <div 
        key={deck.id}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', deck.id);
          setDraggedDeckId(deck.id);
        }}
        onDragEnd={() => setDraggedDeckId(null)}
        onClick={() => setSelectedDeckId(deck.id)}
        className={`
          group flex items-center justify-between p-2 md:p-3 rounded-lg cursor-pointer border transition-all
          ${selectedDeckId === deck.id 
            ? 'bg-purple-900/20 border-purple-500/50' 
            : 'bg-transparent border-transparent hover:bg-zinc-800'
          }
        `}
      >
        <div className="flex items-center gap-3 overflow-hidden flex-1">
          <LayoutGrid size={16} className={selectedDeckId === deck.id ? 'text-purple-400 shrink-0' : 'text-zinc-600 shrink-0'} />
          <div className="flex flex-col min-w-0 flex-1">
            {editingDeckId === deck.id ? (
              <div className="flex items-center gap-2">
                <input 
                  type="text" 
                  autoFocus
                  value={editDeckName} 
                  onChange={e => setEditDeckName(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleRenameDeck(deck.id);
                    if (e.key === 'Escape') setEditingDeckId(null);
                  }}
                  className="bg-black border border-purple-500 rounded px-1 text-sm text-white outline-none font-rajdhani w-full"
                />
                <button onClick={(e) => { e.stopPropagation(); handleRenameDeck(deck.id); }} className="text-green-400 hover:text-green-300"><Check size={14}/></button>
                <button onClick={(e) => { e.stopPropagation(); setEditingDeckId(null); }} className="text-zinc-400 hover:text-white"><X size={14}/></button>
              </div>
            ) : (
              <span className={`text-xs md:text-sm font-bold font-rajdhani truncate ${selectedDeckId === deck.id ? 'text-white' : 'text-zinc-400'}`}>
                {deck.name}
              </span>
            )}
            <span className="text-[9px] md:text-[10px] text-zinc-600 font-orbitron tracking-wider">
              {deck.tricks.length} Tricks
            </span>
          </div>
        </div>
        
        {!editingDeckId && (
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-all shrink-0">
            <button 
              onClick={(e) => { e.stopPropagation(); setEditingDeckId(deck.id); setEditDeckName(deck.name); }}
              className="p-1.5 hover:bg-blue-900/30 text-zinc-600 hover:text-blue-400 rounded transition-all"
            >
              <Edit2 size={14} />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); handleDeleteDeck(deck.id); }}
              className="p-1.5 hover:bg-red-900/30 text-zinc-600 hover:text-red-500 rounded transition-all"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-2 md:p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 md:mb-6 shrink-0 gap-2">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-orbitron uppercase tracking-widest text-xs md:text-sm hidden sm:inline">Back</span>
        </button>
        <h2 className="text-lg md:text-3xl font-orbitron font-black text-white neon-glow-blue uppercase text-center leading-tight">
          Deck Command Center
        </h2>
        <div className="w-8 md:w-20"></div> 
      </div>

      <div className="flex-grow flex flex-col md:flex-row gap-4 md:gap-6 min-h-0 overflow-y-auto md:overflow-hidden">
        
        {/* Left Sidebar: Deck List 
            On mobile: Let it grow naturally 
            On desktop: Full height
        */}
        <div className="w-full md:w-1/3 min-h-[300px] md:h-full bg-zinc-900/80 border border-zinc-800 rounded-xl flex flex-col overflow-hidden shrink-0">

          {/* Custom Decks Header */}
          <div className="p-2 md:p-3 bg-black/40 border-b border-zinc-800 flex justify-between items-center mt-0 shrink-0">
            <h3 className="text-zinc-500 font-orbitron text-[10px] md:text-xs uppercase tracking-widest px-1">
              Custom Decks ({decks.length})
            </h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                className="text-yellow-400 hover:text-yellow-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Create Folder"
              >
                <FolderPlus size={16} />
              </button>
              <button 
                onClick={() => setIsCreatingDeck(!isCreatingDeck)}
                className="text-cyan-400 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Create Deck"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Create Folder Form */}
          {isCreatingFolder && (
            <form onSubmit={handleCreateFolder} className="p-2 md:p-3 bg-zinc-800/50 border-b border-zinc-700 animate-in slide-in-from-top-2 shrink-0">
               <input 
                 autoFocus
                 type="text"
                 value={newFolderName}
                 onChange={e => setNewFolderName(e.target.value)}
                 placeholder="Folder Name..."
                 className="w-full bg-black/50 border border-zinc-600 rounded px-2 py-1 text-sm text-yellow-100 focus:border-yellow-500 outline-none mb-2 font-rajdhani"
               />
               <div className="flex gap-2 justify-end">
                 <button 
                    type="button"
                    onClick={() => setIsCreatingFolder(false)}
                    className="text-xs text-zinc-400 hover:text-white px-2 py-1"
                 >
                   Cancel
                 </button>
                 <button 
                    type="submit"
                    className="text-xs bg-yellow-600 hover:bg-yellow-500 text-black font-bold px-3 py-1 rounded"
                 >
                   Create
                 </button>
               </div>
            </form>
          )}

          {/* Create Deck Form */}
          {isCreatingDeck && (
            <form onSubmit={handleCreateDeck} className="p-2 md:p-3 bg-zinc-800/50 border-b border-zinc-700 animate-in slide-in-from-top-2 shrink-0">
               <input 
                 autoFocus
                 type="text"
                 value={newDeckName}
                 onChange={e => setNewDeckName(e.target.value)}
                 placeholder="Deck Name..."
                 className="w-full bg-black/50 border border-zinc-600 rounded px-2 py-1 text-sm text-white focus:border-cyan-500 outline-none mb-2 font-rajdhani"
               />
               <div className="flex gap-2 justify-end">
                 <button 
                    type="button"
                    onClick={() => setIsCreatingDeck(false)}
                    className="text-xs text-zinc-400 hover:text-white px-2 py-1"
                 >
                   Cancel
                 </button>
                 <button 
                    type="submit"
                    className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1 rounded"
                 >
                   Create
                 </button>
               </div>
            </form>
          )}

          {/* Custom Deck List - Scrollable Area */}
          <div 
            className={`flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar min-h-0 relative transition-colors ${dragOverFolderId === 'root' ? 'bg-cyan-900/10' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedDeckId) setDragOverFolderId('root');
            }}
            onDragLeave={() => {
              if (dragOverFolderId === 'root') setDragOverFolderId(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedDeckId) {
                const updatedDecks = decks.map(d => d.id === draggedDeckId ? { ...d, folderId: null } : d);
                updateUser(updatedDecks, folders);
              }
              setDragOverFolderId(null);
            }}
          >
             {decks.length === 0 && folders.length === 0 && !isCreatingDeck && !isCreatingFolder && (
               <div className="text-center text-zinc-500 text-xs md:text-sm p-4 font-rajdhani italic">
                 No custom decks or folders.
               </div>
             )}

             {/* Render Folders First */}
             {folders.map(folder => {
               const folderDecks = decks.filter(d => d.folderId === folder.id);
               const isExpanded = expandedFolders[folder.id];
               const isDragOver = dragOverFolderId === folder.id;

               return (
                 <div key={folder.id} className="space-y-1">
                   <div 
                     onClick={() => setExpandedFolders(prev => ({ ...prev, [folder.id]: !isExpanded }))}
                     onDragOver={(e) => {
                       e.preventDefault();
                       e.stopPropagation();
                       if (draggedDeckId) setDragOverFolderId(folder.id);
                     }}
                     onDragLeave={() => {
                       if (dragOverFolderId === folder.id) setDragOverFolderId(null);
                     }}
                     onDrop={(e) => {
                       e.preventDefault();
                       e.stopPropagation();
                       if (draggedDeckId) {
                         const updatedDecks = decks.map(d => d.id === draggedDeckId ? { ...d, folderId: folder.id } : d);
                         updateUser(updatedDecks, folders);
                         setExpandedFolders(prev => ({ ...prev, [folder.id]: true }));
                       }
                       setDragOverFolderId(null);
                     }}
                     className={`
                       group flex items-center justify-between p-2 md:p-3 rounded-lg cursor-pointer border transition-all
                       ${isDragOver ? 'bg-cyan-900/30 border-cyan-500/50' : 'bg-black/20 border-transparent hover:bg-zinc-800'}
                     `}
                   >
                     <div className="flex items-center gap-3 overflow-hidden flex-1">
                       <div className="text-yellow-500 shrink-0">
                         {isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />}
                       </div>
                       <div className="flex flex-col min-w-0 flex-1">
                         {editingFolderId === folder.id ? (
                           <div className="flex items-center gap-2">
                             <input 
                               type="text" 
                               autoFocus
                               value={editFolderName} 
                               onChange={e => setEditFolderName(e.target.value)}
                               onClick={e => e.stopPropagation()}
                               onKeyDown={e => {
                                 if (e.key === 'Enter') handleRenameFolder(folder.id);
                                 if (e.key === 'Escape') setEditingFolderId(null);
                               }}
                               className="bg-black border border-yellow-500 rounded px-1 text-sm text-white outline-none font-rajdhani w-full"
                             />
                             <button onClick={(e) => { e.stopPropagation(); handleRenameFolder(folder.id); }} className="text-green-400 hover:text-green-300"><Check size={14}/></button>
                             <button onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); }} className="text-zinc-400 hover:text-white"><X size={14}/></button>
                           </div>
                         ) : (
                           <span className="text-xs md:text-sm font-bold font-rajdhani truncate text-yellow-100">
                             {folder.name}
                           </span>
                         )}
                         <span className="text-[9px] md:text-[10px] text-zinc-500 font-orbitron tracking-wider">
                           {folderDecks.length} Decks
                         </span>
                       </div>
                     </div>
                     
                     {!editingFolderId && (
                       <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-all shrink-0">
                         <button 
                           onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditFolderName(folder.name); }}
                           className="p-1.5 hover:bg-blue-900/30 text-zinc-600 hover:text-blue-400 rounded transition-all"
                         >
                           <Edit2 size={14} />
                         </button>
                         <button 
                           onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                           className="p-1.5 hover:bg-red-900/30 text-zinc-600 hover:text-red-500 rounded transition-all"
                         >
                           <Trash2 size={14} />
                         </button>
                       </div>
                     )}
                   </div>

                   {/* Folder Contents */}
                   {isExpanded && (
                     <div className="pl-4 space-y-1 border-l-2 border-zinc-800 ml-2 mt-1">
                       {folderDecks.length === 0 ? (
                         <div className="text-[10px] text-zinc-600 italic py-1 font-rajdhani">Empty Folder</div>
                       ) : (
                         folderDecks.map(deck => renderDeckItem(deck))
                       )}
                     </div>
                   )}
                 </div>
               );
             })}

             {/* Render Root Decks */}
             {decks.filter(d => !d.folderId).map(deck => renderDeckItem(deck))}
          </div>
        </div>

        {/* Right Area: Trick Editor 
            On mobile: Let it grow naturally 
        */}
        <div className="w-full md:w-2/3 flex-1 min-h-[400px] md:min-h-0 bg-zinc-900/50 border border-zinc-800 rounded-xl flex flex-col overflow-hidden relative">
          
          {!selectedDeckId ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600 font-rajdhani gap-4">
               <LayoutGrid size={48} className="opacity-20" />
               <p>Select a deck to edit.</p>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="p-3 md:p-4 border-b border-zinc-800 bg-black/20 flex flex-col gap-3 md:gap-4 shrink-0">
                 <div className="flex justify-between items-center">
                    <div className="flex flex-col min-w-0 mr-2">
                        <span className="font-orbitron text-[10px] md:text-xs uppercase tracking-widest truncate text-purple-400">
                            Editing Custom Deck
                        </span>
                        <h3 className="text-white text-lg md:text-xl font-bold font-rajdhani truncate">{getCurrentDeckName()}</h3>
                    </div>
                    
                    <div className="flex items-center gap-2 md:gap-3 shrink-0 flex-wrap justify-end">
                        <div className="flex items-center gap-2">
                            <input type="file" accept=".xlsx, .xls, .csv" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                            <button onClick={() => fileInputRef.current?.click()} className="flex items-center justify-center gap-2 bg-green-900/30 text-green-400 border border-green-500/50 px-3 py-1 rounded-full hover:bg-green-900/50 transition-colors text-[10px] md:text-xs font-orbitron uppercase">
                                <FileSpreadsheet size={12} /> Import Excel
                            </button>
                        </div>
                        <div className="px-2 md:px-3 py-1 bg-zinc-800 rounded-full text-[10px] md:text-xs font-orbitron text-zinc-400 border border-zinc-700 whitespace-nowrap">
                           {currentTricks.length} / 120
                        </div>
                    </div>
                 </div>

                 <form onSubmit={handleAddTrick} className="flex gap-2">
                    <input 
                      type="text" 
                      value={newTrick}
                      onChange={(e) => setNewTrick(e.target.value)}
                      className="flex-1 bg-black/50 border rounded-lg px-3 py-2 md:px-4 md:py-3 text-sm md:text-base text-white focus:outline-none focus:ring-1 font-rajdhani border-zinc-700 focus:border-purple-500 focus:ring-purple-500"
                      placeholder="New trick..."
                      autoFocus
                    />
                    <button 
                      type="submit"
                      disabled={currentTricks.length >= 120}
                      className="px-3 py-2 md:px-4 text-white rounded-lg flex items-center justify-center transition-colors shadow-lg bg-purple-600 hover:bg-purple-500 shadow-[0_0_10px_rgba(147,51,234,0.4)] disabled:bg-zinc-700 disabled:text-zinc-500 disabled:shadow-none"
                    >
                      <Plus size={20} className="md:w-6 md:h-6" />
                    </button>
                  </form>
              </div>

              {/* Trick List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar bg-black/20 min-h-0">
                {currentTricks.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-zinc-600 italic font-rajdhani opacity-50 text-sm">
                    Deck is empty. Add some tricks!
                  </div>
                ) : (
                  currentTricks.map((trick, idx) => (
                    <div 
                      key={idx} 
                      className="group flex items-center justify-between bg-zinc-900/80 border border-zinc-800 p-2 md:p-3 rounded-lg hover:border-zinc-600 transition-colors animate-in fade-in slide-in-from-bottom-2 duration-300"
                      style={{ animationDelay: `${idx * 0.03}ms` }}
                    >
                      <span className="text-zinc-200 font-rajdhani text-base md:text-lg pl-2">{trick}</span>
                      <button 
                        onClick={() => handleDeleteTrick(idx)}
                        className="text-zinc-600 hover:text-red-500 transition-colors p-2 opacity-100 md:opacity-50 md:group-hover:opacity-100 hover:bg-red-900/20 rounded"
                      >
                        <Trash2 size={16} className="md:w-[18px] md:h-[18px]" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

      </div>

    </div>
  );
};

export default CustomDeckEditor;
